import React, { useState, useEffect } from 'react';
import { UserX, UserCheck } from 'lucide-react';
import flows from './data/flows.json';

export default function App() {
  const [session, setSession] = useState(() => {
    try {
      const saved = localStorage.getItem('advisso-session');
      return saved ? JSON.parse(saved) : {
        selectedOption: '',
        showChat: false,
        chatStep: 0,
        conversation: [],
        userEmail: null
      };
    } catch (e) {
      return {
        selectedOption: '',
        showChat: false,
        chatStep: 0,
        conversation: [],
        userEmail: null
      };
    }
  });

  const [typedMessage, setTypedMessage] = useState('');
  const [showLightbox, setShowLightbox] = useState(false);

  const { selectedOption, showChat, chatStep, conversation, userEmail } = session;

  const fullMessage = "¿Quieres que te enviemos esta conversación a tu email y/o poder continuar con esta conversación en el futuro? (Puedes introducir tu correo en cualquier momento.)";
  const options = Object.keys(flows);

  // Helper to update session state and persist to localStorage
  const updateSession = (newSessionData) => {
    setSession((prev) => {
      const updated = { ...prev, ...newSessionData };
      localStorage.setItem('advisso-session', JSON.stringify(updated));
      return updated;
    });
  };

  const handleSelect = (e) => {
    const value = e.target.value;
    updateSession({
      selectedOption: value,
      showChat: true
    });
  };

  useEffect(() => {
    if (showChat && chatStep === 0) {
      let i = 0;
      const timer = setInterval(() => {
        setTypedMessage(fullMessage.substring(0, i));
        i++;
        if (i > fullMessage.length) {
          clearInterval(timer);
          updateSession({ chatStep: 1 });
        }
      }, 30);
      return () => clearInterval(timer);
    }
  }, [showChat, chatStep]);

  const typeMessage = async (text, id) => {
    let currentText = '';
    for (let i = 0; i < text.length; i++) {
      currentText += text.charAt(i);
      setSession((prev) => {
        const updatedConversation = prev.conversation.map((msg) => 
          msg.id === id ? { ...msg, typedText: currentText } : msg
        );
        const updated = { ...prev, conversation: updatedConversation };
        localStorage.setItem('advisso-session', JSON.stringify(updated));
        return updated;
      });
      await new Promise((resolve) => setTimeout(resolve, 30));
    }
  };

  const handleUserResponse = async (response) => {
    let nextMessage = "";
    const contextName = flows[selectedOption]?.context_name || selectedOption;
    
    if (response === "Si") {
      nextMessage = "Introduce tu dirección de correo electrónico.";
    } else {
      nextMessage = `Recuerda que en cualquier momento durante la conversación puedes introducir tu dirección de correo electrónico para guardar tus avances haciendo click en . Continuemos a ayudarte con ${contextName}.`;
    }
    
    const userMsgId = Date.now();
    const aiMsgId = Date.now() + 1;

    // First append both messages to conversation (AI message starts with typing=true)
    const newConversation = [
      ...conversation,
      { id: userMsgId, type: 'user', text: response },
      { id: aiMsgId, type: 'ai', text: nextMessage, typedText: '', typing: true }
    ];

    updateSession({
      chatStep: 2,
      conversation: newConversation
    });

    // Run the typing animation
    await typeMessage(nextMessage, aiMsgId);
    
    // Set typing=false once finished
    setSession((prev) => {
      const updatedConversation = prev.conversation.map((msg) => 
        msg.id === aiMsgId ? { ...msg, typing: false } : msg
      );
      const updated = { ...prev, conversation: updatedConversation };
      localStorage.setItem('advisso-session', JSON.stringify(updated));
      return updated;
    });
  };

  const handleReset = () => {
    localStorage.removeItem('advisso-session');
    setSession({
      selectedOption: '',
      showChat: false,
      chatStep: 0,
      conversation: [],
      userEmail: null
    });
    setTypedMessage('');
    setShowLightbox(false);
  };

  const handleEmailSubmit = (e) => {
    if (e.key === 'Enter' && e.target.value.trim() !== '') {
      updateSession({ userEmail: e.target.value });
      e.target.value = ''; // Clear input field
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center p-4 md:p-6 lg:p-8">
      {/* Logo Area */}
      <div className={`transition-all duration-500 flex flex-col items-center ${showChat ? 'mt-4 scale-50' : 'mt-20 md:mt-40'}`}>
        <img src="/advisso-ai-logo-big.png" alt="Advisso AI Logo" className="h-48 md:h-64" />
        {!showChat && <p className="text-palette1 text-lg md:text-xl mt-6">La plataforma de optimización financiera para todos.</p>}
      </div>

      {/* Main Content */}
      <div className="w-full max-w-sm md:max-w-xl lg:max-w-2xl text-center space-y-4 mt-8 md:mt-12">
        {!showChat && (
          <select 
            onChange={handleSelect}
            className="w-full p-3 md:p-4 bg-palette3 text-palette2 border border-palette1 rounded-lg text-base md:text-lg focus:outline-none"
          >
            <option value="" disabled selected>Necesito ayuda con:</option>
            {options.map((option, index) => (
              <option key={index} value={option}>{option}</option>
            ))}
          </select>
        )}

        {/* Chat Interface */}
        {showChat && (
          <div className="mt-8 text-left space-y-6">
            <div className="p-4 bg-[#35353A] rounded-lg">
              <p className="text-palette2">{typedMessage || (conversation.length > 0 ? fullMessage : '')}</p>
            </div>
            
            {/* Conversation list */}
            {conversation.map((msg) => (
              <div key={msg.id} className={`${msg.type === 'user' ? 'text-right' : ''} mb-4`}>
                <div className={`inline-block p-4 rounded-lg ${msg.type === 'user' ? 'bg-palette1 text-palette3' : 'bg-[#35353A] text-palette2'}`}>
                  {msg.typing ? msg.typedText : msg.text.split("haciendo click en")[0]}
                  {!msg.typing && msg.text.includes("haciendo click en") && (
                    <>
                      haciendo click en <UserX className="inline" size={16} />.
                      {msg.text.split("haciendo click en")[1].replace(".", "")}
                    </>
                  )}
                </div>
              </div>
            ))}
            
            {/* Prompts at the bottom */}
            {chatStep === 1 && (
              <div className="fixed bottom-24 w-full max-w-sm md:max-w-xl lg:max-w-2xl px-4 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
                <div className="p-4 bg-palette3 rounded-lg border border-palette1 flex gap-4 justify-center w-full">
                  <button onClick={() => handleUserResponse("Si")} className="px-6 py-2 bg-palette1 text-palette3 rounded-full font-bold">Si</button>
                  <button onClick={() => handleUserResponse("No")} className="px-6 py-2 bg-palette1 text-palette3 rounded-full font-bold">No</button>
                </div>
                <button onClick={() => setShowLightbox(true)} className="text-palette4 hover:text-palette2 text-sm mt-2">Empezar de nuevo</button>
              </div>
            )}
            
            {chatStep === 2 && (
              <div className="fixed bottom-24 w-full max-w-sm md:max-w-xl lg:max-w-2xl px-4 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
                <input 
                  type="email" 
                  onKeyDown={handleEmailSubmit}
                  className="w-full p-4 bg-palette3 text-palette2 border border-palette1 rounded-lg focus:outline-none"
                />
                <button onClick={() => setShowLightbox(true)} className="text-palette4 hover:text-palette2 text-sm mt-2">Empezar de nuevo</button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Lightbox / Modal Modal */}
      {showLightbox && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <div className="bg-palette3 border border-palette1 p-6 rounded-lg max-w-md w-full text-center space-y-6">
            <p className="text-palette2 text-lg">
              {!userEmail ? (
                "¿Deseas empezar de nuevo? Perderás todos tus cambios si no proporcionas tu correo electrónico."
              ) : (
                <>
                  ¿Deseas empezar de nuevo? Puedes continuar con tus conversaciones haciendo click en <UserCheck className="inline ml-1 text-palette2" size={20} />.
                </>
              )}
            </p>
            <div className="flex gap-4 justify-center">
              <button onClick={handleReset} className="px-6 py-2 bg-red-600 text-white rounded-full font-bold hover:bg-red-700 transition-colors">
                Empezar de nuevo
              </button>
              <button onClick={() => setShowLightbox(false)} className="px-6 py-2 bg-palette1 text-palette3 rounded-full font-bold hover:opacity-90 transition-opacity">
                Continuar aquí
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Auth Icon */}
      <button 
        onClick={() => console.log('Auth icon clicked')}
        className="absolute top-4 right-4 text-palette4 hover:text-palette2 transition-colors"
        aria-label="Iniciar sesión"
      >
        <UserX size={28} />
      </button>

      {/* Footer Links */}
      <footer className="absolute bottom-4 w-full px-4 flex flex-wrap justify-center gap-4 md:gap-6 text-palette4 text-xs md:text-sm">
        <a href="/privacidad" className="hover:text-palette2">Política de privacidad</a>
        <a href="/terminos" className="hover:text-palette2">Términos y Condiciones</a>
        <a href="/aviso" className="hover:text-palette2">Aviso Legal</a>
        <a href="/contacto" className="hover:text-palette2">Contacto</a>
      </footer>
    </div>
  );
}
